import { Button, Spinner } from '@1trading/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { PageHeader } from '@/components/layout/page-header';
import { BrokerCard } from '@/features/brokers/list/broker-card';
import { NewBrokerDialog } from '@/features/brokers/list/new-broker-dialog';
import { AlertsCard } from '@/features/compliance-alerts/alerts-card';
import { useBrokers } from '@/hooks/queries/brokers/use-brokers';
import { getErrorMessage } from '@/lib/api/error';

const FETCH_ERROR_TOAST_ID = 'brokers-fetch-error';

// Alerts card, Add broker button, then the responsive broker card grid.
const BrokersPage = () => {
  const { data: brokers, isPending, isError, error, refetch } = useBrokers();
  const [isNewBrokerOpen, setIsNewBrokerOpen] = useState(false);

  useEffect(() => {
    if (isError) toast.error('Failed to fetch brokers', { id: FETCH_ERROR_TOAST_ID });
  }, [isError]);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader documentTitle="Brokers - Bespoke Ops Portal - One Trading" title="Brokers" />

      {/* Legacy bug fix: BopComplianceAlerts was wrapped in v-if="!isLoading",
          blinking the unrelated broker review alerts out on every list (re)load — render
          unconditionally; the card manages its own loading/error/retry. */}
      <AlertsCard alertType="broker-alert" />

      {isPending ? (
        // Loading panel while the initial GET is in flight. Once data exists,
        // the grid stays mounted through background refetches — it never blanks.
        <div className="flex h-64 items-center justify-center">
          <Spinner className="size-8" />
        </div>
      ) : (
        <>
          <div>
            <Button onClick={() => setIsNewBrokerOpen(true)} type="button">
              Add Broker
            </Button>
          </div>

          {isError ? <QueryErrorBanner message={`Failed to fetch brokers: ${getErrorMessage(error)}`} onRetry={() => refetch()} /> : null}

          {brokers ? (
            brokers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No brokers.</p>
            ) : (
              // Legacy Vuetify cols 12/6/4/3 → 1 col mobile, 2 ≥sm, 3 ≥md, 4 ≥lg
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {brokers.map((broker) => (
                  <BrokerCard broker={broker} key={broker.s} />
                ))}
              </div>
            )
          ) : null}
        </>
      )}

      {/* Mounted only while open — closing by any means (Cancel, backdrop, Esc) unmounts
          and therefore fully resets the form */}
      {isNewBrokerOpen ? <NewBrokerDialog onClose={() => setIsNewBrokerOpen(false)} /> : null}
    </div>
  );
};
BrokersPage.displayName = 'BrokersPage';

export default BrokersPage;
